import React, { useState, useEffect, useMemo, Fragment } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus, Edit, Trash2, Users, Shield, UserCheck, UserX,
  Search, Download, MoreHorizontal, KeyRound, PowerOff,
  ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown,
  Code2, Palette, Megaphone, FileText, Wrench, LineChart, Eye,
  UserPlus2, X, UsersRound,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { ActiveFilterChips } from '../components/ActiveFilterChips';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import UserDetailDrawer from '../components/UserDetailDrawer';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format, formatDistanceToNow, parseISO, isToday, isYesterday } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mobile country codes — mirrors the list on the self-service My Profile page.
const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA' }, { code: '+44', country: 'UK' },
  { code: '+91', country: 'India' }, { code: '+61', country: 'Australia' },
  { code: '+49', country: 'Germany' }, { code: '+33', country: 'France' },
  { code: '+81', country: 'Japan' }, { code: '+86', country: 'China' },
  { code: '+971', country: 'UAE' }, { code: '+966', country: 'Saudi' },
  { code: '+65', country: 'Singapore' }, { code: '+27', country: 'South Africa' },
];

// ---------------------------------------------------------------------------
// Design tokens (premium SaaS aesthetic)
// ---------------------------------------------------------------------------
const ROLE_STYLES = {
  admin:            { chip: 'bg-purple-100 text-purple-700 border-purple-200',   dot: 'bg-purple-500',   Icon: Shield },
  department_head:  { chip: 'bg-rose-100 text-rose-700 border-rose-200',         dot: 'bg-rose-500',     Icon: Users },
  team_lead:        { chip: 'bg-orange-100 text-orange-700 border-orange-200',   dot: 'bg-orange-500',   Icon: UserCheck },
  seo_analyst:      { chip: 'bg-sky-100 text-sky-700 border-sky-200',            dot: 'bg-sky-500',      Icon: LineChart },
  developer:        { chip: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', Icon: Code2 },
  uiux_designer:    { chip: 'bg-indigo-100 text-indigo-700 border-indigo-200',   dot: 'bg-indigo-500',   Icon: Palette },
  social_media:     { chip: 'bg-cyan-100 text-cyan-700 border-cyan-200',         dot: 'bg-cyan-500',     Icon: Megaphone },
  technical_seo:    { chip: 'bg-amber-100 text-amber-700 border-amber-200',      dot: 'bg-amber-500',    Icon: Wrench },
  off_page_seo:     { chip: 'bg-teal-100 text-teal-700 border-teal-200',         dot: 'bg-teal-500',     Icon: LineChart },
  user:             { chip: 'bg-slate-100 text-slate-700 border-slate-200',      dot: 'bg-slate-500',    Icon: Users },
  read_only:        { chip: 'bg-slate-100 text-slate-500 border-slate-200',      dot: 'bg-slate-400',    Icon: Eye },
};

// Deterministic pastel avatar colors keyed by user id
const AVATAR_PALETTE = [
  'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-orange-500',
  'bg-pink-500', 'bg-cyan-500', 'bg-rose-500', 'bg-indigo-500',
  'bg-amber-500', 'bg-teal-500', 'bg-fuchsia-500', 'bg-lime-600',
];
const colorForId = (id = '') => AVATAR_PALETTE[
  Math.abs([...id].reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_PALETTE.length
];
const initialsOf = (name = '') => name
  .split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

const formatLastLogin = (iso) => {
  if (!iso) return { label: 'Never', tone: 'text-slate-400' };
  try {
    const d = parseISO(iso);
    if (isToday(d)) return { label: `Today ${format(d, 'hh:mm a')}`, tone: 'text-slate-700' };
    if (isYesterday(d)) return { label: `Yesterday ${format(d, 'hh:mm a')}`, tone: 'text-slate-700' };
    return { label: `${formatDistanceToNow(d)} ago`, tone: 'text-slate-500' };
  } catch { return { label: '—', tone: 'text-slate-400' }; }
};

const Avatar = ({ user, size = 40 }) => {
  const cls = `rounded-full flex items-center justify-center text-white font-semibold shrink-0 overflow-hidden ${colorForId(user?.id)}`;
  const style = { width: size, height: size, fontSize: Math.max(11, size * 0.35) };
  if (user?.profile_picture) {
    return <div className={cls} style={style}>
      <img src={user.profile_picture} alt={user.full_name} className="w-full h-full object-cover" />
    </div>;
  }
  return <div className={cls} style={style}>{initialsOf(user?.full_name)}</div>;
};

const RolePill = ({ role }) => {
  const style = ROLE_STYLES[role?.name] || ROLE_STYLES.user;
  const { Icon } = style;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${style.chip}`}>
      <Icon size={12} />
      {role?.display_name || role?.name || 'Unknown'}
    </span>
  );
};

const StatusDot = ({ status }) => {
  const active = (status || 'Active') === 'Active';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${active ? 'text-emerald-600' : 'text-red-600'}`}>
      <span className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-red-500'}`} />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
};

const SortIcon = ({ current, dir, col }) => {
  if (current !== col) return <ArrowUpDown size={12} className="text-slate-400 opacity-60" />;
  return dir === 'asc'
    ? <ArrowUp size={12} className="text-blue-500" />
    : <ArrowDown size={12} className="text-blue-500" />;
};

// ---------------------------------------------------------------------------
// KPI stat card
// ---------------------------------------------------------------------------
const StatCard = ({ label, value, Icon, tone, testId, hint }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-3xl font-bold text-slate-900 mt-2">{value}</p>
        {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon size={18} />
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const UserManagement = () => {
  const { token, user: currentUser } = useAuth();

  // --- Data ---
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teamLeads, setTeamLeads] = useState([]);
  const [eligibleManagers, setEligibleManagers] = useState({ grouped: [], managers: [] });
  const [loading, setLoading] = useState(true);

  // --- Table state ---
  const [tab, setTab] = useState('active'); // 'active' | 'inactive'
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState([]);
  const [deptFilter, setDeptFilter] = useState([]);
  const [reportsToFilter, setReportsToFilter] = useState([]);
  const [sortBy, setSortBy] = useState('full_name');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // --- Modal state ---
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '', email: '', password: '', rbac_role_id: '', reports_to_user_id: '',
    mobile_number: '', mobile_country_code: '+91', department_id: '', status: 'Active',
  });

  // --- Detail Drawer state ---
  const [drawerUser, setDrawerUser] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const openDrawer = (u) => { setDrawerUser(u); setIsDrawerOpen(true); };
  const closeDrawer = () => setIsDrawerOpen(false);

  // --- Bulk selection state ---
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignLead, setBulkAssignLead] = useState('none');
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelectOne = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const clearSelection = () => setSelectedIds([]);


  // Re-sync the drawer's user object whenever the users list refreshes so the
  // drawer never shows stale role/status/reports_to after an inline edit.
  useEffect(() => {
    if (!drawerUser?.id) return;
    const fresh = users.find(u => u.id === drawerUser.id);
    if (fresh && fresh !== drawerUser) setDrawerUser(fresh);
  }, [users, drawerUser?.id]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [usersRes, rolesRes, deptsRes, leadsRes, mgrsRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rbac/roles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rbac/departments`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { departments: [] } })),
        axios.get(`${API}/users/team-leads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/eligible-managers`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setUsers(usersRes.data || []);
      const roleList = (rolesRes.data?.roles || rolesRes.data || []).filter(r => r.is_active !== false);
      roleList.sort((a, b) => (a.hierarchy_level || 99) - (b.hierarchy_level || 99));
      setRoles(roleList);
      setDepartments(deptsRes.data?.departments || deptsRes.data || []);
      setTeamLeads(leadsRes.data || []);
      setEligibleManagers(mgrsRes.data || { grouped: [], managers: [] });
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Refetch eligible managers when the modal role changes so hierarchy scope stays accurate.
  useEffect(() => {
    const refetch = async () => {
      if (!token || !isDialogOpen) return;
      try {
        const roleName = roles.find(r => r.id === formData.rbac_role_id)?.name;
        const params = {};
        if (roleName) params.role_name = roleName;
        if (editingUser?.id) params.exclude_user_id = editingUser.id;
        const r = await axios.get(`${API}/users/eligible-managers`, {
          headers: { Authorization: `Bearer ${token}` }, params,
        });
        setEligibleManagers(r.data || { grouped: [], managers: [] });
      } catch { /* non-fatal */ }
    };
    refetch();
  }, [isDialogOpen, formData.rbac_role_id, editingUser?.id, token, roles]);

  // --- Helpers ---
  const roleById = (id) => roles.find(r => r.id === id);
  const deptById = (id) => departments.find(d => d.id === id);
  const userById = (id) => users.find(u => u.id === id);

  // Social Media `full_view` toggle — flips the permission for the WHOLE
  // role (affects every user sharing it). Switch is disabled while the
  // request is in flight; local `roles` state only updates after the API
  // call succeeds, so every row/drawer referencing that role reflects it
  // immediately afterward without a full reload.
  const [fullViewBusyRoleId, setFullViewBusyRoleId] = useState(null);
  const toggleRoleFullView = async (roleId, enabled) => {
    if (!roleId) return;
    setFullViewBusyRoleId(roleId);
    try {
      await axios.post(
        `${API}/rbac/roles/${roleId}/social-media-full-view`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoles(prev => prev.map(r => r.id === roleId ? { ...r, social_media_full_view: enabled } : r));
      const r = roleById(roleId);
      toast.success(
        enabled
          ? `Full View enabled for ${r?.display_name || 'role'} — they now see ALL social media posts.`
          : `Full View disabled for ${r?.display_name || 'role'} — back to their normal scoped view.`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update Full View');
    } finally {
      setFullViewBusyRoleId(null);
    }
  };

  const managerOf = (user) => {
    if (!user?.reports_to_user_id) return null;
    const mgr = userById(user.reports_to_user_id);
    if (!mgr) return null;
    const role = roleById(mgr.rbac_role_id);
    return { ...mgr, role_display: role?.display_name || role?.name };
  };

  const roleHidesReportsTo = (roleId) => roleById(roleId)?.name === 'admin';
  const roleRequiresReportsTo = (roleId) => {
    const r = roleById(roleId);
    if (!r) return false;
    return !['admin', 'department_head', 'team_lead', 'read_only'].includes(r.name);
  };

  // --- Stats (computed from full user set, not filtered) ---
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter(u => (u.status || 'Active') === 'Active').length;
    const inactive = total - active;
    const teamLeadRoleId = roles.find(r => r.name === 'team_lead')?.id;
    const leads = teamLeadRoleId
      ? users.filter(u => u.rbac_role_id === teamLeadRoleId && (u.status || 'Active') === 'Active').length
      : 0;
    return { total, active, inactive, leads };
  }, [users, roles]);

  // --- Filtered + sorted users ---
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = users.filter(u => {
      const isActive = (u.status || 'Active') === 'Active';
      if (tab === 'active' && !isActive) return false;
      if (tab === 'inactive' && isActive) return false;
      if (roleFilter.length > 0 && !roleFilter.includes(u.rbac_role_id)) return false;
      if (deptFilter.length > 0 && !deptFilter.includes(u.department_id)) return false;
      if (reportsToFilter.length > 0) {
        const matches = reportsToFilter.some((v) => (v === 'unassigned' && !u.reports_to_user_id) || u.reports_to_user_id === v);
        if (!matches) return false;
      }
      if (q) {
        const roleName = roleById(u.rbac_role_id)?.display_name || '';
        const hay = `${u.full_name} ${u.email} ${roleName}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    list.sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case 'role': va = roleById(a.rbac_role_id)?.display_name || ''; vb = roleById(b.rbac_role_id)?.display_name || ''; break;
        case 'department': va = deptById(a.department_id)?.name || ''; vb = deptById(b.department_id)?.name || ''; break;
        case 'created_at': va = a.created_at || ''; vb = b.created_at || ''; break;
        case 'last_login_at': va = a.last_login_at || ''; vb = b.last_login_at || ''; break;
        default: va = (a.full_name || '').toLowerCase(); vb = (b.full_name || '').toLowerCase();
      }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [users, tab, search, roleFilter, deptFilter, reportsToFilter, sortBy, sortDir, roles, departments]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const paginated = filteredUsers.slice(pageStart, pageStart + pageSize);

  // --- Actions ---
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  };
  const resetFilters = () => {
    setSearch(''); setRoleFilter([]); setDeptFilter([]); setReportsToFilter([]);
    setSortBy('full_name'); setSortDir('asc'); setPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.email.endsWith('.local')) {
      toast.error('Please use a valid email domain (.com, .org, etc.). .local domains are not supported.');
      return;
    }
    if (!formData.rbac_role_id) { toast.error('Please select a Role'); return; }
    if (!editingUser && roleRequiresReportsTo(formData.rbac_role_id) && !formData.reports_to_user_id) {
      toast.error('Please select a manager in "Reports To" for this role.'); return;
    }
    const payload = { ...formData };
    if (roleHidesReportsTo(payload.rbac_role_id) || !payload.reports_to_user_id) {
      payload.reports_to_user_id = null;
    }
    payload.department_id = payload.department_id || null;
    payload.mobile_number = payload.mobile_number?.trim() || null;
    if (!payload.mobile_number) payload.mobile_country_code = null;
    try {
      if (editingUser) {
        const upd = { ...payload }; delete upd.password;
        await axios.put(`${API}/users/${editingUser.id}`, upd, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('User updated!');
      } else {
        await axios.post(`${API}/users`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('User created successfully!');
      }
      setIsDialogOpen(false); resetForm(); loadData();
    } catch (error) {
      const d = error.response?.data?.detail;
      const msg = Array.isArray(d) ? d[0]?.msg : (typeof d === 'string' ? d : 'Failed to save user');
      toast.error(msg);
    }
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser.id) { toast.error('You cannot delete your own account'); return; }
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User deleted!'); loadData();
    } catch { toast.error('Failed to delete user'); }
  };

  const handleToggleStatus = async (user) => {
    const nextStatus = (user.status || 'Active') === 'Active' ? 'Inactive' : 'Active';
    if (!window.confirm(`${nextStatus === 'Inactive' ? 'Deactivate' : 'Reactivate'} ${user.full_name}?`)) return;
    try {
      await axios.put(`${API}/users/${user.id}`, { status: nextStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`User ${nextStatus === 'Active' ? 'reactivated' : 'deactivated'}`); loadData();
    } catch { toast.error('Failed to update user status'); }
  };

  const handleResetPassword = (u) => {
    if (!window.confirm(`Send a password reset email to ${u.email}?`)) return Promise.resolve();
    return axios.post(`${API}/auth/forgot-password`, { email: u.email })
      .then(() => toast.success('Password reset email sent'))
      .catch(() => toast.error('Failed to trigger password reset'));
  };

  const resetForm = () => {
    const defaultRole = roles[roles.length - 1];
    setFormData({
      full_name: '', email: '', password: '', rbac_role_id: defaultRole?.id || '', reports_to_user_id: '',
      mobile_number: '', mobile_country_code: '+91', department_id: '', status: 'Active',
    });
    setEditingUser(null);
  };

  const openEditDialog = (u) => {
    setEditingUser(u);
    setFormData({
      full_name: u.full_name, email: u.email, password: '',
      rbac_role_id: u.rbac_role_id || '',
      reports_to_user_id: u.reports_to_user_id || '',
      mobile_number: u.mobile_number || '',
      mobile_country_code: u.mobile_country_code || '+91',
      department_id: u.department_id || '',
      status: u.status || 'Active',
    });
    setIsDialogOpen(true);
  };

  // Quick inline "create a new department" — a lightweight stand-in for a
  // full department-management screen (out of scope here). Manager/Admin
  // only, same audience as this entire Add/Edit User form.
  const [creatingDept, setCreatingDept] = useState(false);
  const handleCreateDepartment = async () => {
    const name = window.prompt('New department name:');
    if (!name || !name.trim()) return;
    setCreatingDept(true);
    try {
      const res = await axios.post(`${API}/rbac/departments`, { name: name.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      const newDept = res.data;
      setDepartments(prev => [...prev, newDept]);
      setFormData(prev => ({ ...prev, department_id: newDept.id }));
      toast.success(`Department "${newDept.name}" created`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create department');
    } finally {
      setCreatingDept(false);
    }
  };

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Department', 'Reports To', 'Status', 'Created', 'Last Login'];
    const rows = filteredUsers.map(u => {
      const mgr = managerOf(u);
      return [
        u.full_name, u.email,
        roleById(u.rbac_role_id)?.display_name || '',
        deptById(u.department_id)?.name || '',
        mgr ? `${mgr.full_name} (${mgr.role_display})` : '',
        u.status || 'Active',
        u.created_at ? format(parseISO(u.created_at), 'yyyy-MM-dd') : '',
        u.last_login_at ? format(parseISO(u.last_login_at), 'yyyy-MM-dd HH:mm') : 'Never',
      ];
    });
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replaceAll('"', '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `users-${format(new Date(), 'yyyy-MM-dd')}.csv`; a.click();
    toast.success('Users exported to CSV');
  };

  // ================== Bulk actions ==================
  const runBulkAction = async (action, extra = {}) => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/users/bulk-action`,
        { user_ids: selectedIds, action, ...extra },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const skipped = data.skipped?.length || 0;
      if (skipped > 0) {
        toast.warning(`${data.applied} applied · ${skipped} skipped. See Activity Log for details.`);
      } else {
        toast.success(`${data.applied} user${data.applied === 1 ? '' : 's'} updated`);
      }
      clearSelection();
      loadData();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };
  const bulkActivate = () => {
    if (!window.confirm(`Reactivate ${selectedIds.length} selected user(s)?`)) return;
    runBulkAction('activate');
  };
  const bulkDeactivate = () => {
    const affected = selectedIds.filter(id => id !== currentUser?.id).length;
    if (affected === 0) { toast.warning('You cannot deactivate yourself. Deselect yourself first.'); return; }
    if (!window.confirm(`Deactivate ${affected} selected user(s)? They will lose login access.`)) return;
    runBulkAction('deactivate');
  };
  const bulkDelete = () => {
    const affected = selectedIds.filter(id => id !== currentUser?.id).length;
    if (affected === 0) { toast.warning('You cannot delete yourself. Deselect yourself first.'); return; }
    if (!window.confirm(`DELETE ${affected} selected user(s)? This cannot be undone. Their tasks will be unassigned.`)) return;
    runBulkAction('delete');
  };
  const submitBulkAssignReportsTo = () => {
    runBulkAction('assign_reports_to', { reports_to_user_id: bulkAssignLead === 'none' ? null : bulkAssignLead });
    setBulkAssignOpen(false);
    setBulkAssignLead('none');
  };


  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (loading) {
    return <div className="flex items-center justify-center h-96 text-slate-500" data-testid="user-mgmt-loading">Loading users…</div>;
  }

  const activeCount = users.filter(u => (u.status || 'Active') === 'Active').length;
  const inactiveCount = users.length - activeCount;

  return (
    <TooltipProvider>
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
            <Users className="text-blue-600" size={28} /> User Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manage users, reporting hierarchy and permissions.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCSV} data-testid="export-users-btn">
            <Download size={16} className="mr-2" /> Export
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            data-testid="add-user-btn"
          >
            <UserPlus2 size={16} className="mr-2" /> Add User
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-total"    label="Total Users" value={stats.total}    Icon={Users}     tone="bg-blue-50 text-blue-600" />
        <StatCard testId="stat-active"   label="Active"      value={stats.active}   Icon={UserCheck} tone="bg-emerald-50 text-emerald-600" hint={`${stats.total ? Math.round(stats.active / stats.total * 100) : 0}% of total`} />
        <StatCard testId="stat-inactive" label="Inactive"    value={stats.inactive} Icon={UserX}     tone="bg-red-50 text-red-600" hint={stats.inactive > 0 ? 'Requiring action' : 'All clear'} />
        <StatCard testId="stat-leads"    label="Team Leads"  value={stats.leads}    Icon={Shield}    tone="bg-orange-50 text-orange-600" hint="Directing teams" />
      </div>

      {/* Tabs + Toolbar + Table wrapper */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        {/* Tabs */}
        <div className="flex items-center border-b border-slate-200 px-4 pt-3">
          {[
            { key: 'active', label: 'Active Users', count: activeCount },
            { key: 'inactive', label: 'Inactive Users', count: inactiveCount },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setPage(1); }}
              className={`relative px-4 py-3 text-sm font-semibold transition-colors ${tab === t.key ? 'text-blue-600' : 'text-slate-500 hover:text-slate-800'}`}
              data-testid={`tab-${t.key}-users`}
            >
              {t.label}
              <span className={`ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-bold ${tab === t.key ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                {t.count}
              </span>
              {tab === t.key && <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-blue-600 rounded-full" />}
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-100">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search users by name or email…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9"
              data-testid="user-search-input"
            />
          </div>
          <MultiSelectFilter
            label="Role"
            placeholder="All Roles"
            triggerClassName="w-44"
            options={roles.map(r => ({ value: r.id, label: r.display_name || r.name }))}
            selectedValues={roleFilter}
            onChange={(v) => { setRoleFilter(v); setPage(1); }}
            testId="filter-role"
          />
          {departments.length > 0 && (
            <MultiSelectFilter
              label="Department"
              placeholder="All Departments"
              triggerClassName="w-44"
              options={departments.map(d => ({ value: d.id, label: d.name }))}
              selectedValues={deptFilter}
              onChange={(v) => { setDeptFilter(v); setPage(1); }}
              testId="filter-department"
            />
          )}
          <MultiSelectFilter
            label="Reports To"
            placeholder="Reports To"
            triggerClassName="w-52"
            options={[{ value: 'unassigned', label: '— Unassigned —' }, ...teamLeads.map(l => ({ value: l.id, label: `${l.full_name}'s Team` }))]}
            selectedValues={reportsToFilter}
            onChange={(v) => { setReportsToFilter(v); setPage(1); }}
            testId="filter-reports-to"
          />
          <button
            onClick={resetFilters}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium px-2 py-1"
            data-testid="reset-filters-btn"
          >
            Reset Filters
          </button>
        </div>

        <div className="px-4">
          <ActiveFilterChips
            chips={[
              roleFilter.length > 0 && { key: 'role', label: `${roleFilter.length} Role${roleFilter.length > 1 ? 's' : ''}`, onRemove: () => setRoleFilter([]) },
              deptFilter.length > 0 && { key: 'department', label: `${deptFilter.length} Department${deptFilter.length > 1 ? 's' : ''}`, onRemove: () => setDeptFilter([]) },
              reportsToFilter.length > 0 && { key: 'reports-to', label: `${reportsToFilter.length} Reports To`, onRemove: () => setReportsToFilter([]) },
            ].filter(Boolean)}
            onClearAll={(roleFilter.length > 0 || deptFilter.length > 0 || reportsToFilter.length > 0) ? resetFilters : null}
          />
        </div>

        {/* Bulk-action floating bar — appears when any row is selected. */}
        {selectedIds.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-2 px-4 py-3 bg-blue-50 border-b border-blue-100"
            data-testid="bulk-toolbar"
          >
            <span className="text-sm font-semibold text-blue-800 mr-2">
              {selectedIds.length} selected
            </span>
            <Button size="sm" variant="outline" onClick={bulkActivate} disabled={bulkBusy} data-testid="bulk-activate">
              <UserCheck size={14} className="mr-1.5" /> Activate
            </Button>
            <Button size="sm" variant="outline" onClick={bulkDeactivate} disabled={bulkBusy} className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="bulk-deactivate">
              <PowerOff size={14} className="mr-1.5" /> Deactivate
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setBulkAssignLead('none'); setBulkAssignOpen(true); }}
              disabled={bulkBusy}
              data-testid="bulk-assign-lead"
            >
              <UsersRound size={14} className="mr-1.5" /> Assign Team Lead
            </Button>
            <Button size="sm" variant="outline" onClick={bulkDelete} disabled={bulkBusy} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="bulk-delete">
              <Trash2 size={14} className="mr-1.5" /> Delete
            </Button>
            <button
              onClick={clearSelection}
              className="ml-auto text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
              data-testid="bulk-clear"
            >
              <X size={14} /> Clear selection
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 border-b border-slate-200 sticky top-0">
              <tr className="text-left">
                <th className="pl-6 pr-2 py-3 w-8">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400 cursor-pointer"
                    checked={paginated.length > 0 && paginated.every(u => selectedIds.includes(u.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const ids = paginated.map(u => u.id);
                        setSelectedIds(prev => [...new Set([...prev, ...ids])]);
                      } else {
                        const idsOnPage = new Set(paginated.map(u => u.id));
                        setSelectedIds(prev => prev.filter(id => !idsOnPage.has(id)));
                      }
                    }}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-500">
                  <button className="flex items-center gap-1.5 hover:text-slate-800" onClick={() => toggleSort('full_name')}>USER <SortIcon current={sortBy} dir={sortDir} col="full_name" /></button>
                </th>
                <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-500">
                  <button className="flex items-center gap-1.5 hover:text-slate-800" onClick={() => toggleSort('role')}>ROLE <SortIcon current={sortBy} dir={sortDir} col="role" /></button>
                </th>
                <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-500" data-testid="col-full-view">
                  FULL VIEW
                </th>
                {departments.length > 0 && (
                  <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-500">
                    <button className="flex items-center gap-1.5 hover:text-slate-800" onClick={() => toggleSort('department')}>DEPT <SortIcon current={sortBy} dir={sortDir} col="department" /></button>
                  </th>
                )}
                <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-500">REPORTS TO</th>
                <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-500">
                  <button className="flex items-center gap-1.5 hover:text-slate-800" onClick={() => toggleSort('created_at')}>CREATED <SortIcon current={sortBy} dir={sortDir} col="created_at" /></button>
                </th>
                <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-500">
                  <button className="flex items-center gap-1.5 hover:text-slate-800" onClick={() => toggleSort('last_login_at')}>LAST LOGIN <SortIcon current={sortBy} dir={sortDir} col="last_login_at" /></button>
                </th>
                <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-500">STATUS</th>
                <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-500 text-right">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginated.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-slate-400" data-testid="users-empty-state">
                      {tab === 'inactive'
                        ? <><UserX size={40} className="text-slate-300" /><p className="text-sm text-slate-500">No inactive users found.</p><p className="text-xs text-slate-400">All users are currently active.</p></>
                        : <><UserPlus2 size={40} className="text-slate-300" /><p className="text-sm text-slate-500">No users match your filters.</p><button onClick={resetFilters} className="text-blue-600 text-xs font-medium">Reset filters</button></>
                      }
                    </div>
                  </td>
                </tr>
              )}
              {paginated.map(user => {
                const role = roleById(user.rbac_role_id);
                const dept = deptById(user.department_id);
                const mgr = managerOf(user);
                const last = formatLastLogin(user.last_login_at);
                const isSelf = user.id === currentUser?.id;
                return (
                  <tr
                    key={user.id}
                    className={`transition-colors group cursor-pointer ${selectedIds.includes(user.id) ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}
                    data-testid={`user-row-${user.id}`}
                    onClick={() => openDrawer(user)}
                  >
                    <td className="pl-6 pr-2 py-4 w-8" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400 cursor-pointer"
                        checked={selectedIds.includes(user.id)}
                        onChange={() => toggleSelectOne(user.id)}
                        data-testid={`row-checkbox-${user.id}`}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar user={user} />
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{user.full_name} {isSelf && <span className="text-[10px] text-blue-500 font-medium ml-1">(You)</span>}</p>
                          <p className="text-xs text-slate-500 truncate">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4"><RolePill role={role} /></td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <div className="inline-flex items-center gap-2" data-testid={`full-view-toggle-${user.id}`}>
                            <Switch
                              checked={!!role?.social_media_full_view || !!role?.social_media_full_view_locked}
                              disabled={!role || role?.social_media_full_view_locked || fullViewBusyRoleId === role?.id}
                              onCheckedChange={(checked) => toggleRoleFullView(role?.id, checked)}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {!role
                            ? 'No role assigned'
                            : role.social_media_full_view_locked
                              ? `${role.display_name || role.name} already sees ALL posts (via its Approve permission) — this is always on.`
                              : <>Toggles org-wide post visibility for EVERY user with the "{role.display_name || role.name}" role, not just {user.full_name}.</>}
                        </TooltipContent>
                      </Tooltip>
                    </td>
                    {departments.length > 0 && (
                      <td className="px-4 py-4 text-slate-700">{dept?.name || <span className="text-slate-400 italic">—</span>}</td>
                    )}
                    <td className="px-4 py-4">
                      {mgr ? (
                        <div className="flex items-center gap-2" data-testid={`user-reports-to-${user.id}`}>
                          <Avatar user={mgr} size={26} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{mgr.full_name}</p>
                            <p className="text-[11px] text-slate-500">{mgr.role_display}</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic" data-testid={`user-reports-to-${user.id}`}>No Manager Assigned</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-slate-600 whitespace-nowrap">
                      {user.created_at ? format(parseISO(user.created_at), 'MMM d, yyyy') : '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={last.tone}>{last.label}</span>
                    </td>
                    <td className="px-4 py-4"><StatusDot status={user.status} /></td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          className="w-8 h-8 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 flex items-center justify-center transition"
                          onClick={() => openEditDialog(user)}
                          title="Edit"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit size={15} />
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-800 hover:bg-slate-100 flex items-center justify-center transition"
                              title="More"
                              data-testid={`user-actions-${user.id}`}
                            >
                              <MoreHorizontal size={16} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem onClick={() => openEditDialog(user)}>
                              <Edit size={14} className="mr-2" /> Edit User
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                if (!window.confirm(`Send a password reset email to ${user.email}?`)) return;
                                axios.post(`${API}/auth/password-reset/request`, { email: user.email })
                                  .then(() => toast.success('Password reset email sent'))
                                  .catch(() => toast.error('Failed to trigger password reset'));
                              }}
                            >
                              <KeyRound size={14} className="mr-2" /> Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {(user.status || 'Active') === 'Active' ? (
                              <DropdownMenuItem
                                className="text-amber-600 focus:text-amber-700"
                                onClick={() => handleToggleStatus(user)}
                                disabled={isSelf}
                              >
                                <PowerOff size={14} className="mr-2" /> Deactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                className="text-emerald-600 focus:text-emerald-700"
                                onClick={() => handleToggleStatus(user)}
                              >
                                <UserCheck size={14} className="mr-2" /> Reactivate
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-red-600 focus:text-red-700"
                              onClick={() => handleDelete(user.id)}
                              disabled={isSelf}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 size={14} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-slate-100">
          <p className="text-xs text-slate-500">
            Showing <span className="font-semibold text-slate-800">{filteredUsers.length === 0 ? 0 : pageStart + 1}</span>
            {' – '}
            <span className="font-semibold text-slate-800">{Math.min(pageStart + pageSize, filteredUsers.length)}</span>
            {' of '}
            <span className="font-semibold text-slate-800">{filteredUsers.length}</span> users
          </p>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              Rows:
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <button
                className="w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 flex items-center justify-center"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                data-testid="pagination-prev"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 h-8 rounded-lg bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">{currentPage}</span>
              <span className="text-xs text-slate-500 px-1">/ {totalPages}</span>
              <button
                className="w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 flex items-center justify-center"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                data-testid="pagination-next"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== Bulk Assign Team Lead Modal ==================== */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-assign-modal">
          <DialogHeader>
            <DialogTitle>Assign Team Lead to {selectedIds.length} user{selectedIds.length === 1 ? '' : 's'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Select the Team Lead that these users should report to. Users whose role does not allow reporting to a Team Lead (Admin, Department Head, Team Lead) will be silently skipped.
            </p>
            <div>
              <Label>Team Lead</Label>
              <Select value={bulkAssignLead} onValueChange={setBulkAssignLead}>
                <SelectTrigger data-testid="bulk-assign-lead-select">
                  <SelectValue placeholder="Select a Team Lead" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Clear (no manager) —</SelectItem>
                  {teamLeads.map(l => (
                    <SelectItem key={l.id} value={l.id} data-testid={`bulk-lead-option-${l.id}`}>
                      {l.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setBulkAssignOpen(false)}>Cancel</Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={submitBulkAssignReportsTo}
                disabled={bulkBusy}
                data-testid="bulk-assign-confirm"
              >
                {bulkBusy ? 'Applying…' : 'Apply'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== Right-Drawer User Detail ==================== */}
      <UserDetailDrawer
        open={isDrawerOpen}
        onOpenChange={(o) => (o ? setIsDrawerOpen(true) : closeDrawer())}
        user={drawerUser}
        role={drawerUser ? roleById(drawerUser.rbac_role_id) : null}
        department={drawerUser ? deptById(drawerUser.department_id) : null}
        manager={drawerUser ? managerOf(drawerUser) : null}
        isSelf={drawerUser?.id === currentUser?.id}
        RoleBadge={RolePill}
        onToggleFullView={toggleRoleFullView}
        fullViewBusy={!!drawerUser && fullViewBusyRoleId === roleById(drawerUser.rbac_role_id)?.id}
        onEdit={(u) => { closeDrawer(); openEditDialog(u); }}
        onDelete={(id) => { handleDelete(id); closeDrawer(); }}
        onToggleStatus={(u) => handleToggleStatus(u)}
        onResetPassword={(u) => handleResetPassword(u)}
      />

      {/* ==================== Add/Edit User Modal ==================== */}
      <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="user-modal">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="John Doe" required data-testid="user-full-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="user@example.com" required disabled={!!editingUser} data-testid="user-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Mobile Number</Label>
              <div className="flex gap-2">
                <Select
                  value={formData.mobile_country_code}
                  onValueChange={(v) => setFormData({ ...formData, mobile_country_code: v })}
                >
                  <SelectTrigger className="w-[4.5rem] flex-shrink-0 px-2" data-testid="user-mobile-country-select">
                    <SelectValue>{formData.mobile_country_code}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CODES.map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.code} {c.country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="tel"
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value.replace(/[^\d]/g, '') })}
                  placeholder="9876543210"
                  data-testid="user-mobile-input"
                />
              </div>
            </div>
            {!editingUser ? (
              <div className="space-y-2">
                <Label>Password</Label>
                <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Minimum 6 characters" minLength={6} required data-testid="user-password-input" />
              </div>
            ) : (
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                <span className="text-xs text-slate-500">Password is hidden for security.</span>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => handleResetPassword(editingUser)}
                  data-testid="user-reset-password-button"
                >
                  <KeyRound size={14} className="mr-1.5" /> Reset Password
                </Button>
              </div>
            )}
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={formData.rbac_role_id}
                onValueChange={(v) => setFormData({ ...formData, rbac_role_id: v, reports_to_user_id: roleById(v)?.name === 'admin' ? '' : formData.reports_to_user_id })}
              >
                <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.id} value={r.id} data-testid={`user-role-option-${r.name}`}>
                      {r.display_name || r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.rbac_role_id && (
                <p className="text-xs text-slate-500">{roleById(formData.rbac_role_id)?.description}</p>
              )}
            </div>

            {!roleHidesReportsTo(formData.rbac_role_id) && (
              <div className="space-y-2" data-testid="reports-to-field">
                <Label>
                  Reports To
                  {roleRequiresReportsTo(formData.rbac_role_id) && !editingUser && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Select
                  value={formData.reports_to_user_id || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, reports_to_user_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="user-reports-to-select"><SelectValue placeholder="Select a manager" /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="none" data-testid="reports-to-option-none">— None —</SelectItem>
                    {eligibleManagers.grouped?.map(group => (
                      <Fragment key={group.role_name}>
                        <div className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 border-t border-slate-100 mt-1 first:mt-0 first:border-t-0">
                          {group.role_display}s
                        </div>
                        {group.users.map(u => (
                          <SelectItem key={u.id} value={u.id} data-testid={`reports-to-option-${u.id}`} className="pl-4">
                            {u.full_name} <span className="text-xs text-slate-400 ml-1">({group.role_display})</span>
                          </SelectItem>
                        ))}
                      </Fragment>
                    ))}
                  </SelectContent>
                </Select>
                {(!eligibleManagers.grouped || eligibleManagers.grouped.length === 0) && (
                  <p className="text-xs text-amber-600">No eligible managers available for this role.</p>
                )}
              </div>
            )}

            <div className="space-y-2" data-testid="department-field">
              <Label>Department</Label>
              <Select
                value={formData.department_id || 'none'}
                onValueChange={(v) => {
                  if (v === '__add_new__') { handleCreateDepartment(); return; }
                  // Radix's hidden native <select> emits '' when value is set to a just-added
                  // option before it registers — ignore it so the inline-created dept stays selected.
                  if (!v) return;
                  setFormData(prev => ({ ...prev, department_id: v === 'none' ? '' : v }));
                }}
              >
                <SelectTrigger data-testid="user-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="department-option-none">— None —</SelectItem>
                  {departments.map(d => (
                    <SelectItem key={d.id} value={d.id} data-testid={`department-option-${d.id}`}>{d.name}</SelectItem>
                  ))}
                  <SelectItem value="__add_new__" disabled={creatingDept} data-testid="department-add-new">
                    + Add New Department
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-testid="account-status-field">
              <Label>Account Status</Label>
              <Select
                value={formData.status}
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger data-testid="user-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active" data-testid="status-option-active">Active</SelectItem>
                  <SelectItem value="Inactive" data-testid="status-option-inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Roles are managed centrally in <strong>User Types Management</strong> (page
                access) and <strong>Permission Matrix</strong> (granular permissions).
              </p>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-user-button">
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
};

export default UserManagement;
